import { useEffect, useState } from "react";
import { GraduationCap, Plus, Trash2, Save, Check, Eye } from "lucide-react";
import client from "../lib/api";

/**
 * Institution admin editor for the college page. Loads the admin's own
 * org (resolved server side from the tenant), lets them rewrite the
 * "about" copy and the list of things the college hopes students do,
 * and saves it back. A live preview mirrors what a student sees. This
 * surface carries no individual data.
 */
export default function AdminCollegeScreen() {
  const [state, setState] = useState({ loading: true, org: null, note: "" });
  const [about, setAbout] = useState("");
  const [expects, setExpects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/institution/college")
      .then((r) => {
        const org = r?.data?.org || null;
        setState({ loading: false, org, note: r?.data?.note || "" });
        if (org) { setAbout(org.about || ""); setExpects(org.expects || []); }
      })
      .catch((e) => {
        const forbidden = e?.response?.status === 403;
        setState({ loading: false, org: null, note: forbidden ? "forbidden" : "error" });
      });
  }, []);

  const setExpect = (i, v) => setExpects((xs) => xs.map((x, idx) => (idx === i ? v : x)));
  const addExpect = () => setExpects((xs) => (xs.length >= 12 ? xs : [...xs, ""]));
  const removeExpect = (i) => setExpects((xs) => xs.filter((_x, idx) => idx !== i));

  const save = async () => {
    setBusy(true); setErr(""); setSaved(false);
    try {
      const cleanExpects = expects.map((x) => x.trim()).filter(Boolean);
      const { data } = await client.put("/institution/college", { about: about.trim(), expects: cleanExpects });
      const org = data?.org || null;
      if (org) { setAbout(org.about || ""); setExpects(org.expects || []); setState((s) => ({ ...s, org })); }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (_e) {
      setErr("That did not save. Give it another go in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <div className="max-w-3xl mx-auto px-5 pt-16" data-testid="admin-college-loading"><div className="k-quiet">Loading...</div></div>;
  }

  if (!state.org) {
    const forbidden = state.note === "forbidden";
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center" data-testid="admin-college-empty">
        <h1 className="klareet-serif mb-2" style={{ fontSize: "1.5rem" }}>
          {forbidden ? "This is an admin surface." : "No college linked to your account."}
        </h1>
        <p className="k-quiet">
          {forbidden
            ? "You need an institution admin account to edit a college page."
            : "Your account is not tied to a college yet, so there is nothing to edit here."}
        </p>
      </div>
    );
  }

  const name = state.org.display_name || "your college";

  return (
    <div className="max-w-6xl mx-auto px-5 pt-8 pb-24" data-testid="admin-college-page">
      <div className="k-label mb-2 flex items-center gap-2"><GraduationCap size={13} /> college page</div>
      <h1 className="klareet-serif mb-1" style={{ fontSize: "clamp(1.7rem, 3.5vw, 2.2rem)", lineHeight: 1.1 }} data-testid="admin-college-title">
        {name}
      </h1>
      <p className="k-hero-sub mb-6">This is what your students see on their "Your college" page. Write it in plain words.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-5" data-testid="admin-college-editor">
          <div className="k-card space-y-3">
            <label className="k-label block">about the college</label>
            <textarea
              className="k-input klareet-serif"
              rows={7}
              maxLength={4000}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="Say who set this up and why it is here, like you would to a student."
              data-testid="admin-college-about"
            />
            <div className="k-quiet !text-xs text-right">{about.length}/4000</div>
          </div>

          <div className="k-card space-y-3">
            <div className="flex items-center justify-between">
              <label className="k-label block !mb-0">what you hope they do here</label>
              <button onClick={addExpect} disabled={expects.length >= 12} className="k-btn k-btn-ghost !py-1 !px-2 text-xs" data-testid="admin-college-add-expect">
                <Plus size={13} /> Add
              </button>
            </div>
            {expects.length === 0 && <div className="k-quiet !text-sm">No lines yet. Add one to get started.</div>}
            <div className="space-y-2">
              {expects.map((line, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`admin-college-expect-row-${i}`}>
                  <input
                    className="k-input flex-1"
                    maxLength={280}
                    value={line}
                    onChange={(e) => setExpect(i, e.target.value)}
                    placeholder="one short expectation"
                    data-testid={`admin-college-expect-${i}`}
                  />
                  <button onClick={() => removeExpect(i)} className="k-btn k-btn-ghost !p-2" aria-label="remove line" data-testid={`admin-college-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {err && <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="admin-college-error">{err}</div>}

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={busy} className="k-btn k-btn-primary" data-testid="admin-college-save">
              <Save size={14} /> {busy ? "Saving" : "Save page"}
            </button>
            {saved && (
              <span className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-teal))" }} data-testid="admin-college-saved">
                <Check size={14} /> Saved. Students see this now.
              </span>
            )}
          </div>
        </div>

        {/* Live preview */}
        <div data-testid="admin-college-preview">
          <div className="k-label mb-2 flex items-center gap-2"><Eye size={13} /> what students see</div>
          <div className="k-card space-y-5" style={{ background: "hsl(var(--klareet-canvas-2))" }}>
            <div className="flex items-center gap-3">
              {state.org.logo_url ? (
                <img src={state.org.logo_url} alt={name} className="h-10 w-10 object-contain rounded-lg" />
              ) : (
                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-lg font-semibold" style={{ background: "hsl(var(--klareet-teal))", color: "white" }}>
                  {name.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="klareet-serif" style={{ fontSize: "1.2rem", color: "hsl(var(--klareet-ink))" }}>{name}</div>
            </div>
            <div>
              <div className="k-label mb-1">about</div>
              <p className="klareet-serif" style={{ lineHeight: 1.65, color: "hsl(var(--klareet-ink))" }} data-testid="admin-college-preview-about">
                {about.trim() || "Add a few words about the college."}
              </p>
            </div>
            <div>
              <div className="k-label mb-2">what they hope you do here</div>
              <ul className="space-y-2">
                {expects.filter((x) => x.trim()).map((line, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal) / 0.15)", color: "hsl(var(--klareet-teal))" }}>
                      <Check size={12} />
                    </span>
                    <span style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
